import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
} from 'firebase/firestore';
import { db } from './config';
import { OrgMember, MemberRole, MemberStatus } from '../types/models';

export class MemberService {
  // Subscribe to all members of an organization (real-time)
  static subscribeToMembers(
    orgId: string,
    callback: (members: OrgMember[]) => void
  ): () => void {
    const membersRef = collection(db, 'organizations', orgId, 'members');
    return onSnapshot(membersRef, (snapshot) => {
      const members = snapshot.docs.map((d) => d.data() as OrgMember);
      callback(members);
    });
  }

  // Subscribe to pending members only
  static subscribeToPendingMembers(
    orgId: string,
    callback: (members: OrgMember[]) => void
  ): () => void {
    const q = query(
      collection(db, 'organizations', orgId, 'members'),
      where('memberStatus', '==', 'pending')
    );
    return onSnapshot(q, (snapshot) => {
      const members = snapshot.docs.map((d) => d.data() as OrgMember);
      callback(members);
    });
  }

  // Approve a pending member with a chosen role
  static async approveMember(
    orgId: string,
    memberId: string,
    role: MemberRole
  ): Promise<void> {
    // Update member subcollection doc
    await setDoc(
      doc(db, 'organizations', orgId, 'members', memberId),
      { memberStatus: 'active' as MemberStatus, role },
      { merge: true }
    );

    // Update user doc
    await setDoc(
      doc(db, 'users', memberId),
      { memberStatus: 'active' as MemberStatus, role },
      { merge: true }
    );
  }

  // Reject a pending member
  static async rejectMember(orgId: string, memberId: string): Promise<void> {
    await setDoc(
      doc(db, 'organizations', orgId, 'members', memberId),
      { memberStatus: 'rejected' as MemberStatus },
      { merge: true }
    );

    await setDoc(
      doc(db, 'users', memberId),
      { memberStatus: 'rejected' as MemberStatus },
      { merge: true }
    );
  }

  // Change a member's role
  static async changeRole(
    orgId: string,
    memberId: string,
    newRole: MemberRole
  ): Promise<void> {
    const adminStatus = newRole === 'admin' ? 1 : 0;

    await setDoc(
      doc(db, 'organizations', orgId, 'members', memberId),
      { role: newRole },
      { merge: true }
    );

    await setDoc(
      doc(db, 'users', memberId),
      { role: newRole, adminStatus },
      { merge: true }
    );
  }

  // Remove a member from an organization
  static async removeMember(orgId: string, memberId: string): Promise<void> {
    await deleteDoc(doc(db, 'organizations', orgId, 'members', memberId));

    await setDoc(
      doc(db, 'users', memberId),
      { organizationID: '', role: undefined, memberStatus: undefined },
      { merge: true }
    );
  }

  // Get a single member doc (for status checks)
  static async getMember(orgId: string, memberId: string): Promise<OrgMember | null> {
    const memberDoc = await getDoc(doc(db, 'organizations', orgId, 'members', memberId));
    if (!memberDoc.exists()) return null;
    return memberDoc.data() as OrgMember;
  }

  // Subscribe to a single member's status (for pending approval screen)
  static subscribeToMemberStatus(
    orgId: string,
    memberId: string,
    callback: (member: OrgMember | null) => void
  ): () => void {
    return onSnapshot(
      doc(db, 'organizations', orgId, 'members', memberId),
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null);
          return;
        }
        callback(snapshot.data() as OrgMember);
      }
    );
  }
}
